import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBusinessPlan } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import TooltipLabel from "./ui/TooltipLabel";
import { 
  ArrowLeft,
  Activity,
  Wallet,
  TrendingDown as BurnIcon,
  Shield,
  Percent,
  DollarSign
} from "lucide-react";
import GaugeChart from 'react-gauge-chart';
import { formatCurrency as formatCurrencyUtil, formatPercentage as formatPercentageUtil } from '../utils/sharedCalculations';
import { usePlanningYears } from '../hooks/usePlanningYears';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FinancialHealthDashboard = () => {
  const navigate = useNavigate();
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [financialKPIs, setFinancialKPIs] = useState(null);
  const planningData = usePlanningYears(currentPlan?.id);
  const { years } = planningData;

  const formatCurrency = (amount, compact = false) => {
    return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, compact);
  };

  const formatPercentage = (value) => {
    return formatPercentageUtil(value, 1);
  };

  // Load financial health data
  const loadHealthData = async () => {
    if (!currentPlan?.id || !years || years.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const currentYear = years[years.length - 1];
      
      // Fetch financial statements
      const response = await axios.get(
        `${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${currentYear}`
      );
      
      const statements = response.data.financial_statements || {};
      const incomeStatement = statements.income_statement || {};
      const balanceSheet = statements.balance_sheet || {};
      const cashFlow = statements.cash_flow || {};
      const keyMetrics = statements.key_metrics || {};
      
      // Extract data
      const currentAssets = balanceSheet.current_assets || 0;
      const currentLiabilities = balanceSheet.current_liabilities || 0;
      const inventory = balanceSheet.inventory || 0;
      const totalDebt = balanceSheet.total_debt || 0;
      const totalEquity = balanceSheet.total_equity || 0;
      const cash = balanceSheet.cash || 0;
      const operatingCF = cashFlow.operating_cash_flow || 0;
      const operatingExpenses = incomeStatement.operating_expenses || 0;
      
      // Calculate KPIs
      const currentRatio = currentLiabilities > 0 ? currentAssets / currentLiabilities : 0;
      const quickRatio = currentLiabilities > 0 ? (currentAssets - inventory) / currentLiabilities : 0;
      const debtToEquity = totalEquity > 0 ? totalDebt / totalEquity : 0;
      const cashRatio = currentLiabilities > 0 ? cash / currentLiabilities : 0;
      const workingCapital = currentAssets - currentLiabilities;
      const monthlyBurnRate = operatingExpenses / 12;
      const cashRunway = monthlyBurnRate > 0 ? cash / monthlyBurnRate : 0;
      const operatingCFRatio = currentLiabilities > 0 ? operatingCF / currentLiabilities : 0;
      
      // Calculate Planning Health Score
      let liquidityScore = 0;
      if (currentRatio >= 2.0) liquidityScore = 100;
      else if (currentRatio >= 1.5) liquidityScore = 80;
      else if (currentRatio >= 1.0) liquidityScore = 60;
      else liquidityScore = 40;
      
      let profitabilityScore = 0;
      const netMargin = keyMetrics.net_margin_percent || 0;
      if (netMargin >= 20) profitabilityScore = 100;
      else if (netMargin >= 10) profitabilityScore = 80;
      else if (netMargin >= 5) profitabilityScore = 60;
      else profitabilityScore = 40;
      
      let leverageScore = 0;
      if (debtToEquity <= 0.5) leverageScore = 100;
      else if (debtToEquity <= 1.0) leverageScore = 80;
      else if (debtToEquity <= 1.5) leverageScore = 60;
      else leverageScore = 40;
      
      let cashRunwayScore = 0;
      if (cashRunway >= 12) cashRunwayScore = 100;
      else if (cashRunway >= 6) cashRunwayScore = 80;
      else if (cashRunway >= 3) cashRunwayScore = 60;
      else cashRunwayScore = 40;
      
      const overallHealthScore = (
        liquidityScore * 0.25 +
        profitabilityScore * 0.25 +
        leverageScore * 0.20 +
        cashRunwayScore * 0.20 +
        50 * 0.10 // Growth placeholder
      );
      
      setHealthData({
        currentRatio,
        quickRatio,
        debtToEquity,
        cashRatio,
        workingCapital,
        monthlyBurnRate,
        cashRunway,
        operatingCFRatio,
        planningHealthScore: {
          overallScore: Math.round(overallHealthScore),
          components: {
            liquidity: { score: Math.round(liquidityScore), value: currentRatio },
            profitability: { score: Math.round(profitabilityScore), value: netMargin },
            leverage: { score: Math.round(leverageScore), value: debtToEquity },
            cashRunway: { score: Math.round(cashRunwayScore), value: cashRunway }
          }
        },
        year: currentYear
      });

      // Fetch Financial Management KPIs
      try {
        const kpiResponse = await axios.get(`${BACKEND_URL}/api/financial-integration/kpis/${currentPlan.id}`);
        setFinancialKPIs(kpiResponse.data.kpis);
      } catch (error) {
        console.error('Error fetching financial management KPIs:', error);
      }
      
    } catch (error) {
      console.error('Error loading health data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHealthData();
  }, [currentPlan?.id, years]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1,2,3].map(i => (
              <div key={i} className="h-48 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/planning-dashboard')}
            className="mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Planning Dashboard
          </Button>
          <TooltipLabel
            label={<h1 className="text-3xl font-bold text-slate-900">Financial Health Dashboard</h1>}
            tooltip="Comprehensive view of your financial health metrics, ratios, and planning health score"
            asChild
          />
          <p className="text-slate-600 mt-1">
            Comprehensive financial health analysis for {currentPlan?.company_name || currentPlan?.name}
          </p>
        </div>
      </div>

      {/* Planning Health Score */}
      {healthData?.planningHealthScore && (
        <Card className="border-t-4 border-t-indigo-500">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="h-5 w-5 mr-2 text-indigo-600" />
              Planning Health Score
            </CardTitle>
            <CardDescription>Weighted composite metric of plan quality ({healthData.year})</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Circular Gauge */}
              <div className="flex flex-col items-center justify-center">
                <div className="w-48 h-48">
                  <GaugeChart 
                    id="planning-health-gauge"
                    nrOfLevels={3}
                    colors={["#ef4444", "#f59e0b", "#10b981"]}
                    arcWidth={0.3}
                    percent={healthData.planningHealthScore.overallScore / 100}
                    textColor="#1e293b"
                    formatTextValue={() => `${healthData.planningHealthScore.overallScore}`}
                    animate={true}
                  />
                </div>
                <Badge 
                  variant={healthData.planningHealthScore.overallScore >= 80 ? "success" : 
                           healthData.planningHealthScore.overallScore >= 60 ? "warning" : "destructive"}
                  className="text-sm px-4 py-1 mt-2"
                >
                  {healthData.planningHealthScore.overallScore >= 80 ? "Excellent Plan Health" : 
                   healthData.planningHealthScore.overallScore >= 60 ? "Good Plan Health" : 
                   "Needs Improvement"}
                </Badge>
              </div>
              
              {/* Component Scores Breakdown */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-slate-700 mb-3">Component Scores:</h4>
                {Object.entries(healthData.planningHealthScore.components).map(([key, data]) => (
                  <TooltipLabel
                    key={key}
                    label={
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className="text-sm font-medium text-slate-700">{data.score}/100</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${data.score >= 80 ? 'bg-green-500' : 
                                        data.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${data.score}%` }}
                          ></div>
                        </div>
                      </div>
                    }
                    tooltip={`Current value: ${typeof data.value === 'number' ? data.value.toFixed(2) : data.value}`}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Financial Health Metrics - 7 Cards */}
      {healthData && (
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Financial Health Metrics ({healthData.year})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Current Ratio */}
            <TooltipLabel
              label={
                <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Current Ratio</p>
                      <p className="text-3xl font-bold text-slate-900">{healthData.currentRatio.toFixed(2)}</p>
                      <Badge variant={healthData.currentRatio >= 2 ? "success" : healthData.currentRatio >= 1 ? "warning" : "destructive"}>
                        {healthData.currentRatio >= 2 ? "Excellent" : healthData.currentRatio >= 1 ? "Fair" : "Critical"}
                      </Badge>
                      <p className="text-xs text-slate-500 mt-2">Liquidity</p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Current Ratio measures your ability to pay short-term debts (due within 1 year) with short-term assets. Formula: Current Assets ÷ Current Liabilities. Example: $200K assets ÷ $100K liabilities = 2.0 ratio. Interpretation: >2.0 = Excellent liquidity (can pay debts 2x over), 1.5-2.0 = Good, 1.0-1.5 = Fair (just enough to cover), <1.0 = Critical (insufficient assets). Creditors want 2.0+. Too high (>3.0) may mean inefficient asset use."
            />

            {/* Quick Ratio */}
            <TooltipLabel
              label={
                <Card className="border-l-4 border-l-cyan-500 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Quick Ratio</p>
                      <p className="text-3xl font-bold text-slate-900">{healthData.quickRatio.toFixed(2)}</p>
                      <Badge variant={healthData.quickRatio >= 1 ? "success" : "destructive"}>
                        {healthData.quickRatio >= 1 ? "Healthy" : "Critical"}
                      </Badge>
                      <p className="text-xs text-slate-500 mt-2">Acid Test</p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Quick Ratio (Acid-Test Ratio) measures ability to pay short-term debts with only liquid assets, excluding inventory which may be hard to sell quickly. Formula: (Current Assets - Inventory) ÷ Current Liabilities. Example: ($200K assets - $50K inventory) ÷ $100K liabilities = 1.5 ratio. More conservative than Current Ratio. Interpretation: >1.0 = Healthy (can pay debts without selling inventory), 0.5-1.0 = Acceptable for some industries, <0.5 = Concerning liquidity. Ideal: 1.0-1.5 for most businesses."
            />

            {/* Debt-to-Equity */}
            <TooltipLabel
              label={
                <Card className="border-l-4 border-l-purple-500 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Debt-to-Equity</p>
                      <p className="text-3xl font-bold text-slate-900">{healthData.debtToEquity.toFixed(2)}</p>
                      <Badge variant={healthData.debtToEquity <= 1 ? "success" : "warning"}>
                        {healthData.debtToEquity <= 1 ? "Excellent" : "High"}
                      </Badge>
                      <p className="text-xs text-slate-500 mt-2">Leverage</p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Debt-to-Equity Ratio shows how much debt you use relative to equity to finance operations. Formula: Total Debt ÷ Total Equity. Example: $100K debt ÷ $200K equity = 0.5 ratio. Interpretation: <0.5 = Conservative (low risk, less leverage), 0.5-1.0 = Moderate (balanced), 1.0-2.0 = Aggressive (high leverage), >2.0 = High risk (debt exceeds equity). Varies by industry: Tech/SaaS prefer <1.0, Manufacturing 1.0-2.0 common. High ratio increases financial risk but can boost returns."
            />

            {/* Working Capital */}
            <TooltipLabel
              label={
                <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Working Capital</p>
                      <p className="text-3xl font-bold text-slate-900">{formatCurrency(healthData.workingCapital, true)}</p>
                      <Badge variant={healthData.workingCapital > 0 ? "success" : "destructive"}>
                        {healthData.workingCapital > 0 ? "Positive" : "Negative"}
                      </Badge>
                      <p className="text-xs text-slate-500 mt-2">Operations</p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Working Capital is the cash available for day-to-day operations after paying short-term debts. Formula: Current Assets - Current Liabilities. Example: $200K assets - $100K liabilities = $100K working capital. This $100K funds inventory, payroll, rent, etc. Positive working capital means you can operate smoothly; negative means you're borrowing to cover daily expenses (danger zone). Growing businesses need increasing working capital. If it's shrinking while revenue grows, you may face cash crisis."
            />

            {/* Cash Runway */}
            <TooltipLabel
              label={
                <Card className="border-l-4 border-l-orange-500 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Cash Runway</p>
                      <p className="text-3xl font-bold text-slate-900">{healthData.cashRunway.toFixed(1)} mo</p>
                      <Badge variant={healthData.cashRunway >= 6 ? "success" : healthData.cashRunway >= 3 ? "warning" : "destructive"}>
                        {healthData.cashRunway >= 6 ? "Healthy" : healthData.cashRunway >= 3 ? "Moderate" : "Critical"}
                      </Badge>
                      <p className="text-xs text-slate-500 mt-2">Survival</p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Cash Runway shows how many months you can operate before running out of cash at current spending rate. Formula: Cash Balance ÷ Monthly Burn Rate. Example: $120K cash ÷ $10K/month burn = 12 months runway. Critical metric for startups and growth companies. Interpretation: 12+ months = Safe (time to hit milestones), 6-12 months = Moderate (plan fundraising/profitability), 3-6 months = Urgent (immediate action needed), <3 months = Crisis (may not survive). Always know your runway!"
            />

            {/* Monthly Burn Rate */}
            <TooltipLabel
              label={
                <Card className="border-l-4 border-l-red-500 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Monthly Burn Rate</p>
                      <p className="text-3xl font-bold text-slate-900">{formatCurrency(healthData.monthlyBurnRate, true)}</p>
                      <p className="text-xs text-slate-500 mt-1">per month</p>
                      <p className="text-xs text-slate-500 mt-2">Cash: {formatCurrency(healthData.cashRatio * healthData.monthlyBurnRate, true)}</p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Monthly Burn Rate is how much cash you spend each month to run the business. Formula: Annual Operating Expenses ÷ 12. Example: $600K annual expenses ÷ 12 = $50K/month burn. Includes: salaries, rent, marketing, software, utilities, etc. (everything except COGS and one-time items). Lower burn extends runway. Track burn vs. revenue: if revenue covers burn, you're default alive; if not, you're default dead unless you raise money or cut costs. Reduce burn by 20% = extend runway by 25%."
            />

            {/* Operating CF Ratio */}
            <TooltipLabel
              label={
                <Card className="border-l-4 border-l-indigo-500 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Operating CF Ratio</p>
                      <p className="text-3xl font-bold text-slate-900">{healthData.operatingCFRatio.toFixed(2)}</p>
                      <Badge variant={healthData.operatingCFRatio >= 1 ? "success" : "warning"}>
                        {healthData.operatingCFRatio >= 1 ? "Strong" : "Weak"}
                      </Badge>
                      <p className="text-xs text-slate-500 mt-2">Cash Coverage</p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Operating Cash Flow Ratio measures your ability to cover short-term debts with cash generated from operations. Formula: Operating Cash Flow ÷ Current Liabilities. Example: $150K operating cash flow ÷ $100K liabilities = 1.5 ratio. This shows cash-generating power (not just accounting profit). Interpretation: >1.0 = Strong (generating enough cash to cover debts), 0.5-1.0 = Adequate (but monitor closely), <0.5 = Weak (may struggle to pay bills). Better than Current Ratio because it uses actual cash, not assets that might not convert to cash quickly."
            />
          </div>
        </div>
      )}

      {/* Financial Management KPIs - 6 Cards */}
      {financialKPIs && (
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Financial Management KPIs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Debt Service Coverage */}
            <TooltipLabel
              label={
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-sm font-medium">Debt Service Coverage</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {financialKPIs.leverage_ratios?.debt_service_coverage_ratio?.toFixed(2) || 'N/A'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Debt Service Coverage Ratio (DSCR) measures ability to pay all debt obligations (principal + interest) from operating income. Formula: Operating Income ÷ Total Debt Service. Example: $120K operating income ÷ $80K debt payments = 1.5 DSCR. Interpretation: >1.25 = Excellent (comfortable margin), 1.15-1.25 = Good, 1.0-1.15 = Tight (just covering), <1.0 = Danger (cannot cover debts). Lenders typically require 1.25+ for loans. If <1.0, you're using working capital or new debt to pay old debt (unsustainable)."
            />

            {/* Interest Coverage */}
            <TooltipLabel
              label={
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-sm font-medium">Interest Coverage</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {financialKPIs.leverage_ratios?.interest_coverage_ratio?.toFixed(2) || 'N/A'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Interest Coverage Ratio shows how easily you can pay interest on outstanding debt. Formula: EBIT (Earnings Before Interest & Tax) ÷ Interest Expense. Example: $100K EBIT ÷ $20K interest = 5.0 coverage. Interpretation: >3.0 = Excellent (earning 3x+ the interest), 2.5-3.0 = Good, 1.5-2.5 = Acceptable but monitor, <1.5 = Risky (struggling to cover interest). If <1.0, you're losing money before considering interest (very dangerous). Falling coverage may signal trouble ahead before it shows in other metrics."
            />

            {/* Return on Equity */}
            <TooltipLabel
              label={
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-sm font-medium">Return on Equity</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {(financialKPIs.profitability_ratios?.return_on_equity * 100)?.toFixed(2) || 'N/A'}%
                      </p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Return on Equity (ROE) measures how efficiently you generate profit from shareholders' investment. Formula: (Net Income ÷ Shareholders' Equity) × 100. Example: $50K profit ÷ $200K equity = 25% ROE. Interpretation: >20% = Excellent (strong returns), 15-20% = Good, 10-15% = Average, <10% = Poor (investors might prefer safer alternatives). Industry benchmarks: Tech 15-25%, Retail 10-20%, Manufacturing 15-30%. Higher than industry average attracts investors. DuPont formula: ROE = Profit Margin × Asset Turnover × Equity Multiplier."
            />

            {/* Return on Assets */}
            <TooltipLabel
              label={
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-sm font-medium">Return on Assets</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {(financialKPIs.profitability_ratios?.return_on_assets * 100)?.toFixed(2) || 'N/A'}%
                      </p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Return on Assets (ROA) measures how efficiently you use total assets to generate profit, regardless of how assets are financed. Formula: (Net Income ÷ Total Assets) × 100. Example: $50K profit ÷ $500K assets = 10% ROA. Interpretation: >10% = Excellent efficiency, 5-10% = Good, <5% = Poor (assets not productive). Industry benchmarks: Tech/SaaS 10-20% (asset-light), Retail 5-10%, Manufacturing 8-15% (asset-heavy). Higher ROA means you need less capital to grow. Compare with ROE to understand leverage impact."
            />

            {/* P/E Ratio */}
            <TooltipLabel
              label={
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-sm font-medium">P/E Ratio</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {financialKPIs.market_ratios?.price_to_earnings?.toFixed(2) || 'N/A'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Price-to-Earnings (P/E) Ratio shows how much investors pay for each dollar of earnings. Formula: Market Price per Share ÷ Earnings per Share. Example: $50 share price ÷ $2 EPS = 25 P/E. Interpretation: High P/E = Market expects high growth (tech often 25-50+), Low P/E = Undervalued or slow growth (value stocks 10-15), Average = Market is ~20-25. Compare with industry peers and growth rate. P/E of 30 with 30% growth rate (PEG = 1.0) is reasonable; P/E of 50 with 10% growth is expensive."
            />

            {/* Dividend Yield */}
            <TooltipLabel
              label={
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-slate-600 text-sm font-medium">Dividend Yield</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {(financialKPIs.market_ratios?.dividend_yield * 100)?.toFixed(2) || 'N/A'}%
                      </p>
                    </div>
                  </CardContent>
                </Card>
              }
              tooltip="Dividend Yield shows the annual dividend income as a percentage of share price. Formula: (Annual Dividends per Share ÷ Price per Share) × 100. Example: $2 annual dividend ÷ $50 share price = 4% yield. Interpretation: High yield (5-8%+) = Mature company, stable income but limited growth; Low yield (0-2%) = Growth company reinvesting profits; 3-5% = Balanced. Compare with bond yields and inflation. Sustainable yield = Dividend ÷ Earnings (payout ratio) should be <70%. Yield >10% often signals trouble (market expects dividend cuts)."
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialHealthDashboard;
